import { PayloadAction } from "@reduxjs/toolkit";
import toolIndex from "toolConstants";
import { camelizeObject } from "utilities/string";
import { values, toPairs, isEmpty, isObjectLike, omit, set } from "lodash";
import { calculateWallSpeakerTargetPoint } from "reduxModules/calculations/roomCalculations";
import { computeAndroidAutoCamera } from "utilities/treeFrogPlatform";
import { resolveBarProductFamily } from "utilities/parleBarBundle";
import { normalizeUcComputeForUcx, UCX_UC_COMPUTE_FALLBACK } from "utilities/treeFrogBundleMatching";

/** Toast shown when the Devio UCX rule switches a stored 'Zoom Rooms' selection. */
const UCX_ZOOM_TOAST = `Zoom Rooms is not available on Devio UCX — UC Compute set to ${UCX_UC_COMPUTE_FALLBACK}.`;

export const deviceConfigReducers = {
  /**
   * Sets the speaker aiming for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string} action.payload - The new speaker aiming setting.
   */
  setSpeakerAiming: (state: any, action: any) => {
    state.areas[state.activeTab].speakerAiming = action.payload;
    state.areas[state.activeTab].customized.qty = false;
  },

  /**
   * Sets the speaker model for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string} action.payload - The new speaker model key (e.g., 'wallspkr_mask6c').
   */
  setSpeakerModel: (state: any, action: any) => {
    const area = state.areas[state.activeTab];
    const newModelKey = action.payload;

    // Update speakers.model (not speakerModel - that's a legacy field)
    area.speakers.model = newModelKey;

    // Get the speaker definition from tool constants
    const tool = state.currentTool;
    const speakerDef = toolIndex[tool]?.constants?.equipment?.speakers?.[newModelKey];

    if (speakerDef) {
      // Update modelName and height from the speaker definition
      area.speakers.modelName = speakerDef.modelName;
      if (speakerDef.height) {
        area.speakers.height = speakerDef.height(area);
      }

      // Reset targetPoint and linking to defaults when switching wall speaker models
      if (speakerDef.isWallSpkr) {
        const constants = toolIndex[tool]?.constants;
        area.speakers.targetPoint = calculateWallSpeakerTargetPoint(area, constants);
        area.speakers.linkedSpeakers = true;
      }
    }

    // Mark as customized
    area.customized.speakers = true;

    // Reset layout to trigger recalculation with new model
    area.speakers.layout = [];
    area.speakers.qty = 0;

    // Call resetArea to apply the model change
    toolIndex[tool].resetArea(state, 'speakers', {
      model: newModelKey,
      modelName: speakerDef?.modelName
    });

    // No Devio UCX revalidation here: the layout was just cleared, so the
    // port-budget check would see 0 speakers and wrongly promote a Tesira +
    // UCC-UCX1000-T fallback design back to Devio (no DSP / amp in the
    // equipment list). `updateDevice` re-runs it once calculateDevicesMiddleware
    // has placed the new speakers.
  },

  /**
   * Deletes a speaker from a specific area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The deletion information.
   * @param {string} action.payload.area - The ID of the area containing the speaker.
   * @param {number} action.payload.id - The index of the speaker to delete.
   */
  deleteSpeaker: (state: any, action: any) => {
    const { area, id } = action.payload;
    if (state.activeTab === area) {
      state.areas[area].speakers.splice(id, 1);
      state.areas[area].remap = true;
      state.areas[area].customized.qty = true;
    }
  },

  /**
   * Toggles the apply delay flag for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object (unused in this reducer).
   */
  setApplyDelay: (state: any, action: any) => {
    state.areas[state.activeTab].applyDelay = !state.areas[state.activeTab].applyDelay
  },

  /**
   * Sets the speaker layout for multiple areas.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Array} action.payload - An array of area objects with updated speaker layouts.
   */
  setSpeakerLayout: (state: any, action: any) => {
    action.payload.forEach((area: any) => {
      area = camelizeObject(area);
      const id = area.id;
      if (!(state.areas[id].recalculate || state.areas[id].remap)) return;
      const remap = state.areas[id].remap;
      // Update specific properties instead of replacing entire object to preserve all data
      const updatedArea = omit(area, ['apiVersion', 'apiMessage']);
      Object.keys(updatedArea).forEach(key => {
        if (typeof updatedArea[key] === 'object' && updatedArea[key] !== null && !Array.isArray(updatedArea[key])) {
          state.areas[id][key] = { ...state.areas[id][key], ...updatedArea[key] };
        } else {
          state.areas[id][key] = updatedArea[key];
        }
      });
      state.areas[id].design = state.areas[id].design || []
      state.areas[id].speakers = state.areas[id].speakers || []
      if (!remap) state.areas[id].speakerModel = area.speakers?.[0].modelName;
      state.areas[id].speakerLayout = state.areas[id].design.map((sub: any, index: number) => {
        const regex = /\s(?<type>[a-z]+)(?<aisleIndex>\d+)/g;
        const { aisleIndex = null } = regex.exec(sub.id)?.groups || {};
        let bbox;
        if (aisleIndex) {
          const { left, top } = state.areas[id].location;
          bbox = state.areas[id].aislesArray[aisleIndex];
          bbox.left += left;
          bbox.top += top;
        } else {
          bbox = { ...state.areas[id].location, ...state.areas[id].dimensions };
        };
        return ({ bbox: bbox, ...sub })
      });
      // Don't change currentDevice here - it should be managed by UI state
      state.areas[id].recalculate = false;
      state.areas[id].remap = false;
    });
  },

  /**
   * Sets the recalculate flag for one or all areas.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The recalculate information.
   * @param {string} [action.payload.area] - The ID of the area to update, or 'current' for the active area.
   * @param {boolean} action.payload.flag - The new recalculate flag value.
   */
  setRecalculate: (state: any, action: PayloadAction<{ area?: string; flag: boolean }>) => {
    const { area = null, flag } = action.payload
    if (area) {
      state.areas[area === 'current' ? state.activeTab : area].recalculate = flag;
    } else {
      values(state.areas).forEach((area: any) => area.recalculate = flag);
    }
  },

  /**
   * Sets the remap flag for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {boolean} action.payload - The new remap flag value.
   */
  setRemap: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    state.areas[state.activeTab].remap = action.payload;
    if (action.payload) {
      const spkr = state.areas[state.activeTab].speakerModel;
      state.areas[state.activeTab].speakers.forEach((speaker: any) => speaker.modelName = spkr);
    }
  },

  /**
   * Sets customized properties for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} [action.payload] - The customized properties to set.
   */
  setCustomized: (state: any, action: PayloadAction<Record<string, any>>) => {
    if (!(state.activeTab in state.areas)) return;
    if (!isEmpty(action.payload)) {
      toPairs(action.payload).forEach(([path, value]) => {
        set(state.areas[state.activeTab].customized, path, true); // set the customized flag
        // Only update device properties if value is a non-empty object
        // Empty arrays or primitives are used just to set the customization flag
        if (isObjectLike(value) && !isEmpty(value) && typeof value === 'object' && value !== null) {
          toPairs(value as Record<string, any>).forEach(([key, v]) => {
            set(state.areas[state.activeTab][path], key, v)
          })
        }
        // REMOVED the else clause that was corrupting device objects
      });
    } else {
      toolIndex[state.currentTool].resetArea(state, null, state.currentDevice);
    };
  },

  setRequestedQuote: (state: any, action: PayloadAction<boolean>) => {
    state.requestedQuote = action.payload
  },

  /**
   * Sets the product family for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string} action.payload - The new product family ('Tesira' or 'Devio').
   */
  setProductFamily: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    const area = state.areas[state.activeTab];
    area.productFamily = action.payload;
    // Family change can make this a Devio UCX design, where 'Zoom Rooms' is not
    // an available UC Compute option (see normalizeUcComputeForUcx).
    if (normalizeUcComputeForUcx(area)) {
      state._toastMessage = UCX_ZOOM_TOAST;
      state._toastSeverity = 'warning';
    }
  },

  /**
   * Sets the UC Compute for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string|Object} action.payload - The UC Compute option ('None', 'Microsoft Teams', or 'Zoom Rooms').
   *                                          Can be a string or an object with ucCompute property.
   */
  setUCCompute: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;

    const area = state.areas[state.activeTab];
    // Handle both direct value and object with ucCompute property
    const value = typeof action.payload === 'object' && action.payload.ucCompute
      ? action.payload.ucCompute
      : action.payload;
    area.ucCompute = value;

    // A single bar mic (CBC-2500, ABC-2500, VBC-2500a, VBC-2800 — no
    // expansion) resolves Family based on this exact value: Windows → always
    // 'Parlé'; Android + Teams/Zoom → 'Devio'; Android + 'None' → 'Parlé'.
    // Non-bar designs, and bar + expansion (the exclusive Devio-UCX-only
    // case owned by revalidateUcxEligibility), are untouched. See
    // utilities/parleBarBundle.js.
    const barFamily = resolveBarProductFamily(area);
    if (barFamily) area.productFamily = barFamily;

    // Picking 'Zoom Rooms' can itself resolve the design to Devio UCX (a single
    // bar mic on Android: Teams/Zoom → 'Devio'), where Zoom Rooms isn't offered.
    // Fall back to Microsoft Teams so state can't hold a Zoom SKU the list no
    // longer shows. See normalizeUcComputeForUcx (utilities/treeFrogBundleMatching.js).
    if (normalizeUcComputeForUcx(area)) {
      state._toastMessage = UCX_ZOOM_TOAST;
      state._toastSeverity = 'warning';
    }
  },

  /**
   * Sets camera properties for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object|boolean} action.payload - The camera settings or boolean for hasCamera.
   */
  setCameras: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    const payload = action.payload;
    const area = state.areas[state.activeTab];

    if (typeof payload === 'boolean') {
      // Handle hasCamera toggle
      if (!area.cameras) {
        area.cameras = {};
      }
      area.cameras.hasCamera = payload;
    } else if (typeof payload === 'object') {
      // Handle camera object updates
      area.cameras = {
        ...area.cameras,
        ...payload
      };

      // On Android (Tree Frog UC) a mic without an integrated camera still requires a standalone
      // Vidi 280 — selecting "None" isn't a valid final state there, so re-apply the auto-camera.
      // This keeps the camera present regardless of how the user reached None, matching the
      // setPlatform / setMicrophones behavior (which is why toggling the platform "fixed" it).
      if (payload.model === 'none' && area.platform === 'Android') {
        const patch = computeAndroidAutoCamera(area, toolIndex[state.currentTool]?.constants);
        if (patch) {
          area.cameras = patch.camera;
          if (area.customized) area.customized.cameras = false;
          state._toastMessage = patch.toast;
          state._toastSeverity = 'warning';
        }
      }
    }
  },
};
